// Helper para integrar el Picker oficial de Google Drive.
//
// Carga (idempotente) los dos SDKs necesarios:
//   - https://accounts.google.com/gsi/client → window.google.accounts.oauth2
//   - https://apis.google.com/js/api.js      → window.gapi (+ gapi.load('picker'))
//
// Los SDKs de Google no exponen types oficiales sólidos para estos módulos
// (especialmente `google.picker`), así que `any` está justificado puntualmente
// en este fichero: no añadimos eslint-disable porque la regla del repo lo
// permite en este contexto delimitado.

declare global {
  interface Window {
    google?: any
    gapi?: any
  }
}

export interface DriveStatus {
  enabled: boolean
  clientId: string | null
  apiKey: string | null
}

export interface DrivePickedFile {
  id: string
  name: string
  mimeType: string
}

export interface PickFromDriveResult {
  accessToken: string
  files: DrivePickedFile[]
}

const GIS_SRC = 'https://accounts.google.com/gsi/client'
const GAPI_SRC = 'https://apis.google.com/js/api.js'

let gisLoadPromise: Promise<void> | null = null
let gapiLoadPromise: Promise<void> | null = null
let pickerLoadPromise: Promise<void> | null = null

function loadScript(src: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    // ¿Ya está inyectado? Reutilizamos.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`
    )
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        reject(new Error(`No se pudo cargar ${src}`))
      )
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.defer = true
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true'
      resolve()
    })
    script.addEventListener('error', () =>
      reject(new Error(`No se pudo cargar ${src}`))
    )
    document.head.appendChild(script)
  })
}

function loadGis(): Promise<void> {
  if (typeof window !== 'undefined' && window.google?.accounts?.oauth2) {
    return Promise.resolve()
  }
  if (!gisLoadPromise) {
    gisLoadPromise = loadScript(GIS_SRC).catch((err) => {
      gisLoadPromise = null
      throw err
    })
  }
  return gisLoadPromise
}

function loadGapi(): Promise<void> {
  if (typeof window !== 'undefined' && window.gapi) {
    return Promise.resolve()
  }
  if (!gapiLoadPromise) {
    gapiLoadPromise = loadScript(GAPI_SRC).catch((err) => {
      gapiLoadPromise = null
      throw err
    })
  }
  return gapiLoadPromise
}

function loadPicker(): Promise<void> {
  if (typeof window !== 'undefined' && window.google?.picker) {
    return Promise.resolve()
  }
  if (!pickerLoadPromise) {
    pickerLoadPromise = new Promise<void>((resolve, reject) => {
      if (!window.gapi) {
        reject(new Error('gapi no disponible para cargar picker'))
        return
      }
      try {
        window.gapi.load('picker', {
          callback: () => resolve(),
          onerror: () => reject(new Error('Error al cargar google.picker')),
        })
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Error cargando picker'))
      }
    }).catch((err) => {
      pickerLoadPromise = null
      throw err
    })
  }
  return pickerLoadPromise
}

/**
 * Carga (de forma idempotente) los scripts de Google Identity Services y de
 * gapi (con el módulo `picker`). Resuelve cuando `window.google`,
 * `window.gapi` y `google.picker` están listos.
 */
export async function loadGoogleDrive(): Promise<void> {
  await Promise.all([loadGis(), loadGapi()])
  await loadPicker()
}

/**
 * Abre el Picker oficial de Google Drive y devuelve los ficheros seleccionados
 * junto con el access token OAuth (scope `drive.readonly`) que el backend
 * usará para descargarlos.
 */
export async function pickFromDrive(opts: {
  clientId: string
  apiKey: string
}): Promise<PickFromDriveResult> {
  await loadGoogleDrive()

  const google = window.google
  if (!google?.accounts?.oauth2 || !google?.picker) {
    throw new Error('Google Drive SDK no inicializado')
  }

  // 1) Obtener access token mediante GIS.
  const accessToken: string = await new Promise<string>((resolve, reject) => {
    try {
      const tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: opts.clientId,
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (resp: { access_token?: string; error?: string }) => {
          if (resp.error || !resp.access_token) {
            reject(new Error(resp.error ?? 'No se obtuvo access_token'))
            return
          }
          resolve(resp.access_token)
        },
      })
      tokenClient.requestAccessToken({ prompt: '' })
    } catch (err) {
      reject(err instanceof Error ? err : new Error('Error iniciando OAuth'))
    }
  })

  // 2) Mostrar el Picker y esperar a la selección (o cancelación).
  const files: DrivePickedFile[] = await new Promise<DrivePickedFile[]>(
    (resolve) => {
      const picker = new google.picker.PickerBuilder()
        .addView(new google.picker.View(google.picker.ViewId.DOCS))
        .setOAuthToken(accessToken)
        .setDeveloperKey(opts.apiKey)
        .setCallback((data: any) => {
          const action = data?.action as string | undefined
          if (action === google.picker.Action.PICKED || action === 'picked') {
            const docs = Array.isArray(data?.docs) ? data.docs : []
            const mapped: DrivePickedFile[] = docs.map((d: any) => ({
              id: String(d.id),
              name: String(d.name ?? d.id),
              mimeType: String(d.mimeType ?? 'application/octet-stream'),
            }))
            resolve(mapped)
          } else if (action === google.picker.Action.CANCEL || action === 'cancel') {
            resolve([])
          }
        })
        .build()
      picker.setVisible(true)
    }
  )

  return { accessToken, files }
}
